/**
 * T-09 · El estado de sesión del panel, con TanStack Query.
 *
 * `GET /yo` es la única fuente: quién eres, qué puedes y qué contrató tu
 * cafetería. **El panel no deduce nada por su cuenta** — si escondiera una
 * sección mirando algo que él mismo calcula, tarde o temprano mostraría algo
 * que el servidor rechaza, o al revés.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { MODULOS } from '@fialrest/shared'
import type { Modulo, EstadoModulo } from '@fialrest/shared'
import { pedir, ErrorDeApi } from '@fialrest/cliente'

const clavesDeModulo = Object.keys(MODULOS) as [Modulo, ...Modulo[]]

/**
 * Espejo del contrato `Yo` de `@fialrest/shared`, como **esquema**. El tipo lo
 * comprueba el compilador; esto comprueba lo que llegó por la red, que es otra
 * cosa y puede no coincidir durante un despliegue.
 */
export const esquemaYo = z.object({
  identidad: z.object({
    staffUserId: z.string(),
    tenantId: z.string(),
  }),
  permisos: z.array(z.string()).readonly(),
  // `partialRecord` y no `record`: una cafetería que no contrató lealtad **no
  // trae esa clave**. Con `record` el tipo diría que están todas, y el front
  // leería `yo.modulos.loyalty` como si siempre existiera — el `undefined`
  // aparecería en pantalla, no aquí. Lo cazó el compilador de las pruebas.
  modulos: z.partialRecord(z.enum(clavesDeModulo), z.enum(['active', 'suspended', 'closed'])),
  //  `T-67` · Hasta dónde llegan sus permisos. Lo usa el selector de sucursal de
  //  la sección Lealtad para saber si «Todas mis sucursales» es una opción suya.
  alcance: z.enum(['tenant', 'branch']),
})

export type Yo = z.infer<typeof esquemaYo>

export const CLAVE_YO = ['yo'] as const

export function useYo() {
  return useQuery({
    queryKey: CLAVE_YO,
    queryFn: ({ signal }) => pedir('/yo', esquemaYo, { señal: signal }),
    // Sin sesión no se reintenta: insistir con una cookie que no vale sólo
    // retrasa la pantalla de entrada y llena el registro del servidor.
    retry: (intentos, error) =>
      !(error instanceof ErrorDeApi && error.esSinSesion) && intentos < 2,
    // Los permisos cambian sin cerrar sesión (T-05), así que conviene revalidar
    // al volver a la pestaña: quien acaba de perder un permiso no debe seguir
    // viendo el botón media hora.
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  })
}

/**
 * Cerrar sesión. **Limpia la caché además de llamar a la API**: sin eso, los
 * datos de la persona anterior siguen en memoria y el siguiente que entre en
 * ese navegador los vería un instante antes de que se recarguen.
 */
export function useSalir() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: () => pedir('/autenticacion/salir', z.undefined(), { metodo: 'POST' }),
    // `onSettled` y no `onSuccess`: si la llamada falla, la cookie puede haberse
    // borrado igual —el servidor la borra siempre— y dejar la caché llena sería
    // peor que limpiarla de más.
    onSettled: () => { cliente.clear() },
  })
}

/** ¿Tiene este permiso? El servidor decide igual; esto sólo evita enseñar botones que no. */
export const puede = (yo: Yo | undefined, permiso: string): boolean =>
  yo?.permisos.includes(permiso) ?? false

/** El estado de un módulo, o `undefined` si la cafetería no lo contrató. */
export const estadoDe = (yo: Yo | undefined, modulo: Modulo): EstadoModulo | undefined =>
  yo?.modulos[modulo]
